import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FieldProps } from 'formik';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { SubscriptionCertificate, CERTIFICATE_STATUS } from '@root/src/types';
import { validateFile } from '@utils/fileValidation';
import Icon, { ICON_NAME } from '@features/ui/components/Icon';
import { textSmall } from '@features/ui/styles/textStyles';
import {
  uploadCertificate,
  uploadSubscriptionCertificate,
  selectCertificateInfo,
  selectCertificateLoading,
  selectCertificateError,
  selectUploadProgress,
} from '@features/eventDetail/slices/certificateSlice';
import { selectCurrentUserDetail } from '@features/users/slices/userSlice';
import { selectSubscriptionFor } from '@features/subscriptions/slices/formSubscriptionSlice';
import InputFileUpload, { InputFileUploadProps } from './InputFileUpload';

interface InputCertificateUploadProps extends Omit<InputFileUploadProps, 'meta'> {
  field?: FieldProps['field'];
  form?: FieldProps['form'];
  meta?: FieldProps['meta'];
  // For subscription detail page - provide certificate data directly
  certificate?: SubscriptionCertificate
  // Additional certificate-specific props
  description?: string;
  documentationUrl?: string;
  // Optional subscription ID for refreshing data after upload
  subscriptionId?: string;
}

const InputCertificateUpload = ({
  field,
  form,
  meta,
  certificate: externalCertificate,
  description,
  documentationUrl,
  subscriptionId,
  ...props
}: InputCertificateUploadProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();

  const profile = useSelector(selectCurrentUserDetail);
  const subscriptionFor = useSelector(selectSubscriptionFor);
  const athleteId = subscriptionFor?._id || profile?._id;

  // Use external certificate data if provided, otherwise use Redux state
  // For subscription context, prioritize Redux state after upload for real-time updates
  const reduxCertificateInfo = useSelector(selectCertificateInfo(athleteId));
  const certificateInfo = (subscriptionId && reduxCertificateInfo)
    ? reduxCertificateInfo
    : (externalCertificate || reduxCertificateInfo);
  const isLoading = useSelector(selectCertificateLoading(athleteId));
  const error = useSelector(selectCertificateError(athleteId));
  const uploadProgress = useSelector(selectUploadProgress(athleteId));
  const [validationError, setValidationError] = useState<string | null>(null);

  // Auto-validate Formik field when certificate exists (event context only)
  useEffect(() => {
    const shouldSetValue = (
      !subscriptionId // Event context (not subscription)
      && !externalCertificate // Not using external certificate prop
      && certificateInfo?._id // Certificate exists
      && form && field // Formik context available
      && form.values[field.name] !== true // Field not yet set to valid
    );

    if (shouldSetValue) {
      // Certificate exists (either already existing or just uploaded), mark field as valid
      form.setFieldError(field.name, undefined);
      form.setFieldValue(field.name, true);
      form.setFieldTouched(field.name, true);
      // Ensure validation runs after state updates
      setTimeout(() => {
        form.validateField(field.name);
      }, 0);
    }
  }, [
    certificateInfo?._id,
    subscriptionId,
    externalCertificate,
    form,
    field,
  ]);

  const handleChange = (file: File | null) => {
    setValidationError(null);

    if (file) {
      const fileValidationError = validateFile(file, t, 10);
      if (fileValidationError) {
        setValidationError(fileValidationError);
        return;
      }
    }

    if (file && athleteId) {
      // Use specific subscription upload action if subscriptionId is provided
      if (subscriptionId) {
        dispatch(uploadSubscriptionCertificate({
          athleteId,
          file,
          subscriptionId,
          locale: router.locale,
        }));
      } else {
        dispatch(uploadCertificate({
          athleteId,
          file,
        }));
      }
    } else if (!file && form && field) {
      // Reset Formik field when file is removed
      form.setFieldValue(field.name, null);
      form.setFieldTouched(field.name, true);
    }
  };

  const handleRemove = () => {
    if (form && field) {
      form.setFieldValue(field.name, null);
      form.setFieldTouched(field.name, true);
    }
    props.onRemove?.();
  };

  const hasCertificate = certificateInfo && (certificateInfo._id || certificateInfo.url);

  const defaultFile = hasCertificate ? {
    name: certificateInfo.url.split('/').pop(),
    url: certificateInfo.url || '',
  } : undefined;

  const getCertificateStatusInfo = (status: CERTIFICATE_STATUS) => {
    switch (status) {
      case CERTIFICATE_STATUS.VALID:
        return {
          color: 'var(--color-green)',
          backgroundColor: 'var(--color-green-light)',
          text: t('certificate_status_valid.message', 'Certificat valide'),
          canReplace: false,
        };
      case CERTIFICATE_STATUS.PENDING:
        return {
          color: 'var(--color-orange)',
          backgroundColor: 'var(--color-orange-light)',
          text: t('certificate_status_pending.message', 'En attente de validation'),
          canReplace: true,
        };
      case CERTIFICATE_STATUS.INVALID:
        return {
          color: 'var(--color-red)',
          backgroundColor: 'var(--color-red-light)',
          text: t('certificate_status_invalid.message', 'Certificat invalide après vérification'),
          canReplace: true,
        };
      default:
        return null;
    }
  };

  const statusInfo = externalCertificate?.status ? getCertificateStatusInfo(externalCertificate.status) : null;

  return (
    <div>
      {statusInfo && (
        <StatusContainer>
          <StatusBadge
            color={statusInfo.color}
            backgroundColor={statusInfo.backgroundColor}
          >
            {statusInfo.text}
          </StatusBadge>
        </StatusContainer>
      )}
      {documentationUrl && (
        <DocumentationLink>
          <a href={documentationUrl} target="_blank" rel="noopener noreferrer">
            {t('certificateUpload_documentation.message')}
            {' '}
            <Icon name={ICON_NAME.externalLink} />
          </a>
        </DocumentationLink>
      )}
      <InputFileUpload
        key={`certificate-upload-${athleteId}`}
        {...props}
        value={null}
        defaultFile={defaultFile}
        onChange={handleChange}
        onRemove={handleRemove}
        disabled={isLoading || props.disabled || (statusInfo && !statusInfo.canReplace)}
        isLoading={isLoading}
        uploadProgress={uploadProgress}
        canReplace={statusInfo ? statusInfo.canReplace : true}
        meta={{
          touched: !!(validationError || error),
          error: validationError || (error && error.endsWith('.message') ? String(t(error.replace('.message', ''))) : error) || false,
        }}
      />
      {description && (
        <Description>{description}</Description>
      )}

    </div>
  );
};

const Description = styled.div`
  ${textSmall};
  color: var(--color-grey4);
  margin-bottom: 8px;
`;

const DocumentationLink = styled.div`
  ${textSmall};
  margin-bottom: 12px;
  
  a {
    color: var(--color-primary);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const StatusBadge = styled.div<{ color: string; backgroundColor: string }>`
  ${textSmall};
  padding: 4px 8px;
  border-radius: 12px;
  color: ${props => props.color};
  background-color: ${props => props.backgroundColor};
  font-weight: 500;
`;

export default InputCertificateUpload;
